import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useForm } from "react-hook-form";
import {
  CustomDrawer,
  CustomModal,
  CustomButton,
  CustomCheckbox,
  CustomMiddleSelect,
  CustomMultiSelect,
  CustomTimeInput,
  CustomCalendarInput,
  CustomFileUploader,
  CustomSectionTitle,
  FixedActionBar,
  colors,
  usePreviewDrawerUnsaved,
} from "@ippon/shared-components";

import type {
  UpsertTaskResultBody,
  TaskResultJudgement,
  ExecutionRecord,
  DailyResult,
} from "@/store/managementTaskResultSlice";
import type { ManagementTaskType } from "@/store/managementTaskSlice";
import { formatDateSlash } from "@ippon/shared-utils";
import { ControlledTextInput, ControlledSingleSelect } from "./ControlledInputs";
import { type CustomFieldValue } from "./CustomFieldRenderer";
import ManagementTaskCustomFieldsAddable, {
  type PendingManagementCustomField,
} from "./ManagementTaskCustomFieldsAddable";
import { createManagementTaskCustomField } from "@/store/managementTaskCustomFieldSlice";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/store";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";

type ManagementFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annual"
  | "always";

interface Props {
  open: boolean;
  onClose: () => void;
  taskName: string;
  property_id: string;
  task_id: string;
  year: number;
  month: number;
  frequency?: ManagementFrequency;
  taskType?: ManagementTaskType;
  initial?: {
    planned: boolean;
    plannedDates?: number[];
    completed: boolean;
    executionRecords?: ExecutionRecord[];
    totalJudgement?: TaskResultJudgement;
    remarks?: string;
    // 法定フラグは ManagementTask.is_legal_inspection に集約済み。月別ドロワーは
    // リモートか否かだけを扱う。
    plannedIsRemote?: boolean;
    actualIsRemote?: boolean;
    customFields?: CustomFieldValue[];
  };
  onSave: (body: UpsertTaskResultBody) => Promise<void>;
  /**
   * "drawer" 既存の月セルクリック時の編集ドロワー (CustomDrawer)
   * "page" /management-task-results/:task_id ページ表示 (PreviewDrawer 埋め込みも兼用)
   */
  displayMode?: "drawer" | "page";
  /** displayMode="page" で PreviewDrawer 等に埋め込まれている場合 true */
  embedded?: boolean;
}

const DAILY_RESULT_OPTIONS: { value: DailyResult; label: string }[] = [
  { value: "良好", label: "良好" },
  { value: "要確認", label: "要確認" },
  { value: "要対応", label: "要対応" },
  { value: "異常あり", label: "異常あり" },
  { value: "その他", label: "その他" },
];

const JUDGEMENT_OPTIONS: { value: TaskResultJudgement; label: string }[] = [
  { value: "合格", label: "合格" },
  { value: "条件付合格", label: "条件付合格" },
  { value: "不合格", label: "不合格" },
];

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

const firstDayOfWeek = (year: number, month: number): number =>
  new Date(year, month - 1, 1).getDay();

const dayFromIso = (iso: string): number => {
  const d = Number(iso.slice(8, 10));
  return Number.isFinite(d) ? d : 0;
};

const buildIso = (year: number, month: number, day: number): string => {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const aggregateJudgement = (
  records: ExecutionRecord[],
): TaskResultJudgement | undefined => {
  let hasFail = false;
  let hasConditional = false;
  let hasPass = false;
  for (const r of records) {
    if (r.result === "異常あり" || r.result === "要対応") hasFail = true;
    else if (r.result === "要確認" || r.result === "その他") hasConditional = true;
    else if (r.result === "良好") hasPass = true;
  }
  if (hasFail) return "不合格";
  if (hasConditional) return "条件付合格";
  if (hasPass) return "合格";
  return undefined;
};

const newRecord = (
  year: number,
  month: number,
  day: number,
): ExecutionRecord => ({
  execution_id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  date: buildIso(year, month, day || 1),
});

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const normalizeExecutionRecords = (records: ExecutionRecord[]) =>
  records.map((record) => ({
    execution_id: record.execution_id,
    date: record.date || "",
    time: record.time || "",
    checker: record.checker || "",
    result: record.result || "",
    findings: record.findings || "",
    finding_status: record.finding_status || "",
    finding_action: record.finding_action || "",
    finding_resolved_date: record.finding_resolved_date || "",
    note: record.note || "",
    attachments: record.attachments || [],
  }));

const normalizeCustomFields = (fields: CustomFieldValue[]) =>
  fields.map((field) => ({
    field_id: field.field_id,
    value: field.value ?? "",
  }));

const normalizePendingFields = (fields: PendingManagementCustomField[]) =>
  fields.map((field) => ({
    temp_id: field.temp_id,
    label: field.label,
    value: field.value ?? "",
  }));

const buildDirtySnapshot = (values: {
  planned: boolean;
  plannedDates: number[];
  executed: boolean;
  executionRecords: ExecutionRecord[];
  totalJudgement: TaskResultJudgement | "";
  remarks: string;
  plannedIsRemote: boolean;
  actualIsRemote: boolean;
  otherAttachments: unknown[];
  resultCustomFields: CustomFieldValue[];
  pendingResultCustomFields: PendingManagementCustomField[];
}): string =>
  safeStringify({
    planned: values.planned,
    plannedDates: [...values.plannedDates].sort((a, b) => a - b),
    executed: values.executed,
    executionRecords: normalizeExecutionRecords(values.executionRecords),
    totalJudgement: values.totalJudgement || "",
    remarks: values.remarks || "",
    plannedIsRemote: values.plannedIsRemote,
    actualIsRemote: values.actualIsRemote,
    otherAttachments: values.otherAttachments,
    resultCustomFields: normalizeCustomFields(values.resultCustomFields),
    pendingResultCustomFields: normalizePendingFields(
      values.pendingResultCustomFields,
    ),
  });

const ManagementResultEditDrawer: React.FC<Props> = ({
  open,
  onClose,
  taskName,
  property_id,
  task_id,
  year,
  month,
  frequency,
  taskType,
  initial,
  onSave,
  displayMode = "drawer",
  embedded = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const [resultCustomFields, setResultCustomFields] = useState<
    CustomFieldValue[]
  >([]);
  const [pendingResultCustomFields, setPendingResultCustomFields] = useState<
    PendingManagementCustomField[]
  >([]);
  const [planned, setPlanned] = useState(false);
  const [plannedDates, setPlannedDates] = useState<number[]>([]);
  const [executed, setExecuted] = useState(false);
  const [executionRecords, setExecutionRecords] = useState<ExecutionRecord[]>(
    [],
  );
  const [totalJudgement, setTotalJudgement] = useState<TaskResultJudgement | "">(
    "",
  );
  const [remarks, setRemarks] = useState("");
  // 月別ドロワーは「リモートか否か」のみを保持。法定フラグは task 側に集約済み。
  const [plannedIsRemote, setPlannedIsRemote] = useState(false);
  const [actualIsRemote, setActualIsRemote] = useState(false);
  const [otherAttachments, setOtherAttachments] = useState<unknown[]>([]);
  const [calendarMode, setCalendarMode] = useState<"simple" | "detail">(
    "detail",
  );
  const [expandedExecId, setExpandedExecId] = useState<string | null>(null);
  const [initialDirtySnapshot, setInitialDirtySnapshot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const initPlanned = !!initial?.planned;
    const initPlannedDates = initial?.plannedDates || [];
    const initRecords = initial?.executionRecords || [];
    const initExecuted = !!initial?.completed || initRecords.length > 0;
    const initTotalJudgement = initial?.totalJudgement || "";
    const initRemarks = initial?.remarks || "";
    const initPlannedIsRemote = !!initial?.plannedIsRemote;
    const initActualIsRemote = !!initial?.actualIsRemote;
    const initCustomFields = initial?.customFields || [];
    setPlanned(initPlanned);
    setPlannedDates(initPlannedDates);
    setExecutionRecords(initRecords);
    setExecuted(initExecuted);
    setTotalJudgement(initTotalJudgement);
    setRemarks(initRemarks);
    setPlannedIsRemote(initPlannedIsRemote);
    setActualIsRemote(initActualIsRemote);
    setOtherAttachments([]);
    setResultCustomFields(initCustomFields);
    // ドロワーを開き直すたびに pending は破棄 (前回入力中の項目を引き継がない)
    setPendingResultCustomFields([]);
    setCalendarMode("detail");
    setInitialDirtySnapshot(
      buildDirtySnapshot({
        planned: initPlanned,
        plannedDates: initPlannedDates,
        executed: initExecuted,
        executionRecords: initRecords,
        totalJudgement: initTotalJudgement,
        remarks: initRemarks,
        plannedIsRemote: initPlannedIsRemote,
        actualIsRemote: initActualIsRemote,
        otherAttachments: [],
        resultCustomFields: initCustomFields,
        pendingResultCustomFields: [],
      }),
    );
  }, [open, initial]);

  const currentDirtySnapshot = useMemo(
    () =>
      buildDirtySnapshot({
        planned,
        plannedDates,
        executed,
        executionRecords,
        totalJudgement,
        remarks,
        plannedIsRemote,
        actualIsRemote,
        otherAttachments,
        resultCustomFields,
        pendingResultCustomFields,
      }),
    [
      planned,
      plannedDates,
      executed,
      executionRecords,
      totalJudgement,
      remarks,
      plannedIsRemote,
      actualIsRemote,
      otherAttachments,
      resultCustomFields,
      pendingResultCustomFields,
    ],
  );

  const isDirty =
    Boolean(initialDirtySnapshot) && currentDirtySnapshot !== initialDirtySnapshot;

  const dayOptions = useMemo(
    () =>
      Array.from({ length: daysInMonth(year, month) }, (_, i) => ({
        value: String(i + 1),
        label: `${i + 1}日`,
      })),
    [year, month],
  );

  // 予定回数: 「実施予定」ON かつ実施予定日が設定されている日数
  const plannedCount = useMemo(
    () => (planned ? plannedDates.length : 0),
    [planned, plannedDates],
  );

  const completedCount = executionRecords.length;

  // 月間サマリー表示テキスト (仕様):
  //   予定回数:
  //     - planned=false                                → 「実施予定なし」
  //     - planned=true  & plannedDates=[]              → 「0回」
  //     - planned=true  & plannedDates=[..]            → 「N回」
  //   実施回数:
  //     - executed=false                               → 「実施なし」
  //     - executed=true & executionRecords=[]          → 「0回」
  //     - executed=true & executionRecords=[..]        → 「N回」
  //   達成率:
  //     - 予定 or 実施のどちらかが 1 件未満             → 「-%」
  //     - 両方 1 件以上                                → 「N%」
  const plannedSummaryText = useMemo(() => {
    if (!planned) return "実施予定なし";
    return `${plannedCount}回`;
  }, [planned, plannedCount]);

  const completedSummaryText = useMemo(() => {
    if (!executed) return "実施なし";
    return `${completedCount}回`;
  }, [executed, completedCount]);

  const achievementText = useMemo(() => {
    if (frequency === "always") return "-%";
    if (plannedCount <= 0 || completedCount <= 0) return "-%";
    return `${Math.round((completedCount / plannedCount) * 100)}%`;
  }, [frequency, plannedCount, completedCount]);

  const aggregatedJudgement = useMemo(
    () => aggregateJudgement(executionRecords),
    [executionRecords],
  );

  const recordsByDay = useMemo(() => {
    const map = new Map<number, ExecutionRecord[]>();
    for (const r of executionRecords) {
      const d = dayFromIso(r.date);
      if (!d) continue;
      const list = map.get(d) || [];
      list.push(r);
      map.set(d, list);
    }
    return map;
  }, [executionRecords]);

  // PreviewDrawer (点検/清掃の月別実績プレビュー) に埋め込まれた page モード時、
  // 編集中(dirty)を親 PreviewDrawer に報告し、閉じ操作で破棄確認を出させる。
  // PreviewDrawer 外 (drawer モード/フルページ) では no-op。
  usePreviewDrawerUnsaved(isDirty);

  const markDirty = () => undefined;

  const addRecordForDay = (day: number) => {
    const rec = newRecord(year, month, day);
    setExecutionRecords((prev) => [...prev, rec]);
    markDirty();
  };

  const removeRecord = (id: string) => {
    setExecutionRecords((prev) => prev.filter((r) => r.execution_id !== id));
    markDirty();
  };

  const updateRecord = (id: string, patch: Partial<ExecutionRecord>) => {
    setExecutionRecords((prev) =>
      prev.map((r) => (r.execution_id === id ? { ...r, ...patch } : r)),
    );
    markDirty();
  };

  const toggleDay = (day: number) => {
    const existing = recordsByDay.get(day) || [];
    if (existing.length === 0) {
      addRecordForDay(day);
    } else {
      // 当日のレコードが1件のみなら消す、複数あるなら詳細モードに切替
      if (existing.length === 1) {
        removeRecord(existing[0].execution_id);
      } else {
        setCalendarMode("detail");
      }
    }
  };

  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const requestClose = () => {
    if (isDirty) {
      setUnsavedConfirmOpen(true);
      return;
    }
    onClose();
  };
  const confirmDiscardAndClose = () => {
    setUnsavedConfirmOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isDirty || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // pending fields の flush: master upsert → 戻りの field_id を custom_fields にマージ
      let mergedCustomFields = resultCustomFields;
      if (pendingResultCustomFields.length > 0) {
        const results = await Promise.all(
          pendingResultCustomFields.map((p) =>
            dispatch(
              createManagementTaskCustomField({
                label: p.label,
                field_type: "text",
                section_key: "result",
                applies_to_task_types: taskType ? [taskType] : undefined,
              }),
            ),
          ),
        );
        const failed = results.find(
          (r) => !createManagementTaskCustomField.fulfilled.match(r),
        );
        if (failed) {
          const msg =
            (failed.payload as string) ||
            "カスタムフォーム項目の追加に失敗しました";
          showToast({ type: "error", message: msg });
          setIsSubmitting(false);
          return;
        }
        const newValues: CustomFieldValue[] = results.map((r, i) => ({
          field_id: (r.payload as { field_id: string }).field_id,
          value: pendingResultCustomFields[i].value as never,
        }));
        mergedCustomFields = [...mergedCustomFields, ...newValues];
        setPendingResultCustomFields([]);
      }
      const records = executed ? executionRecords : [];
      const body: UpsertTaskResultBody = {
        property_id,
        task_id,
        year,
        month,
        planned,
        planned_dates: plannedDates,
        // 実施済みチェック ON だけで完了扱い。日付未入力 (records 空) でも
        // completed: true で保存し、表側は既存の `-日` フォールバックで表示する。
        completed: executed,
        execution_records: records,
        total_judgement: totalJudgement || aggregatedJudgement || undefined,
        remarks: remarks.trim() || undefined,
        // 法定フラグは task 側 (is_legal_inspection) に集約済みのため送らない。
        planned_is_remote:
          taskType === "点検" && planned ? plannedIsRemote : undefined,
        actual_is_remote:
          taskType === "点検" && executed ? actualIsRemote : undefined,
        custom_fields: mergedCustomFields.map((c) => ({
          field_id: c.field_id,
          value: c.value as never,
        })),
      };
      await onSave(body);
      setInitialDirtySnapshot(currentDirtySnapshot);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // 実施予定日は frequency に関わらず常に複数選択可。
  // (daily で「1日に N 回」の場合でも、月内で複数日に予定が立つケースを許容)
  const showPlannedDateMulti = frequency !== "always";

  const renderSummaryHeading = (label: string) => (
    <Box sx={{ mb: 2 }}>
      <CustomSectionTitle title={label} size="small" />
    </Box>
  );

  const renderSummaryCard = (
    value: string | number,
    label: string,
    color?: string,
  ) => (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        p: 1,
        borderRadius: "4px",
        backgroundColor: colors.background.lighter,
      }}
    >
      <Typography sx={{ fontSize: "11px", color: colors.text.secondary }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          color: color || colors.text.primary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const renderCalendar = () => {
    const days = daysInMonth(year, month);
    const firstDow = firstDayOfWeek(year, month);
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    return (
      <Box
        data-testid="management-result-calendar"
        sx={{
          backgroundColor: "#F8F8F8",
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          p: 1.5,
        }}
      >
        <Box sx={{ position: "relative", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: colors.text.primary,
              textAlign: "center",
            }}
          >
            {year}年{month}月
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  border: `1.5px solid ${colors.primary.main}`,
                  borderRadius: "3px",
                  boxSizing: "border-box",
                }}
              />
              <Typography
                sx={{ fontSize: "11px", color: colors.text.secondary }}
              >
                予定日
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors.primary.main,
                  borderRadius: "3px",
                }}
              />
              <Typography
                sx={{ fontSize: "11px", color: colors.text.secondary }}
              >
                実施日
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0.5,
            mb: 0.5,
          }}
        >
          {WEEK_LABELS.map((w) => (
            <Typography
              key={w}
              sx={{
                fontSize: "11px",
                color: colors.text.secondary,
                textAlign: "center",
              }}
            >
              {w}
            </Typography>
          ))}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0.5,
          }}
        >
          {cells.map((d, idx) => {
            if (d === null) {
              return <Box key={`e-${idx}`} sx={{ height: 36 }} />;
            }
            const recs = recordsByDay.get(d) || [];
            const isPlanned = plannedDates.includes(d);
            const isDone = recs.length > 0;
            let bg: string = "#FFFFFF";
            let fg: string = colors.text.primary;
            // 全セル 1px ボーダーで揃えてサイズの凸凹を防止する
            let borderColor: string = colors.border.light;
            let fontWeight: number = 400;
            if (isDone) {
              bg = colors.primary.main;
              fg = colors.text.inverse;
              borderColor = colors.primary.main;
            } else if (isPlanned) {
              borderColor = colors.primary.main;
              fg = colors.primary.main;
              fontWeight = 700;
            }
            return (
              <Box
                key={d}
                onClick={() => toggleDay(d)}
                data-testid="management-result-calendar-day"
                data-day={d}
                sx={{
                  height: 36,
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: bg,
                  color: fg,
                  border: `1px solid ${borderColor}`,
                  boxSizing: "border-box",
                  position: "relative",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight }}
                >
                  {d}
                </Typography>
                {recs.length > 1 && (
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 1,
                      right: 3,
                      fontSize: "9px",
                      fontWeight: 700,
                    }}
                  >
                    ×{recs.length}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const drawerFooter = (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ py: "0px" }}
      data-testid="management-result-save-button"
    >
      <Can action="management-task:edit">
        <CustomButton
          label="保存"
          buttonCategory="submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!isDirty || isSubmitting}
        />
      </Can>
    </Stack>
  );

  const unsavedConfirmModal = (
    <CustomModal
      openModal={unsavedConfirmOpen}
      handleCloseModal={() => setUnsavedConfirmOpen(false)}
      title="ページ移動の確認"
      bodyText="入力中の情報が失われますが、よろしいでしょうか？"
      modalType="deleteModal"
      bodyTextAlign="left"
      sx={{ padding: "0px !important" }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <CustomButton
          label="戻る"
          buttonCategory="cancelModal"
          onClick={() => setUnsavedConfirmOpen(false)}
          sx={{ width: "50% !important" }}
        />
        <CustomButton
          label="移動"
          buttonCategory="confirm"
          onClick={confirmDiscardAndClose}
          sx={{ width: "50% !important" }}
        />
      </Box>
    </CustomModal>
  );

  const formBody = (
    <Box data-testid="management-result-drawer">
        <Stack spacing={2}>
          {/* 月間サマリー */}
          {frequency !== "always" && (
            <Box>
              {renderSummaryHeading("月間サマリー")}
              <Stack
                direction="row"
                spacing={2}
                data-testid="management-result-summary-cards"
              >
                {renderSummaryCard(
                  plannedSummaryText,
                  "予定回数",
                  colors.primary.main,
                )}
                {renderSummaryCard(
                  completedSummaryText,
                  "実施回数",
                  colors.text.primary,
                )}
                {renderSummaryCard(
                  achievementText,
                  "達成率",
                  colors.status.success,
                )}
              </Stack>
            </Box>
          )}

          {/* 予定セクション */}
          <Box data-testid="management-result-planned-section">
            {renderSummaryHeading("予定")}
            <Stack spacing={1.8}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid="management-result-input-planned"
              >
                <CustomCheckbox
                  checked={planned}
                  onChange={() => {
                    setPlanned((p) => !p);
                    markDirty();
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: colors.text.primary,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => {
                    setPlanned((p) => !p);
                    markDirty();
                  }}
                >
                  実施予定
                </Typography>
              </Stack>
              {/* 点検タスクで実施予定 ON のとき、その月の「予定」がリモート点検かどうかを選択 */}
              {taskType === "点検" && planned && (
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ mb: 0.5 }}
                  data-testid="management-result-planned-inspection-flags"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CustomCheckbox
                      checked={plannedIsRemote}
                      onChange={() => {
                        setPlannedIsRemote((p) => !p);
                        markDirty();
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: colors.text.primary,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => {
                        setPlannedIsRemote((p) => !p);
                        markDirty();
                      }}
                    >
                      リモート点検
                    </Typography>
                  </Stack>
                </Stack>
              )}
              {planned && showPlannedDateMulti && (
                <Box data-testid="management-result-input-planned-dates">
                  <ManagedMultiSelect
                    label="実施予定日"
                    value={plannedDates.map(String)}
                    onChange={(arr) => {
                      setPlannedDates(arr.map(Number).sort((a, b) => a - b));
                      markDirty();
                    }}
                    options={dayOptions}
                    placeholder="日付を選択 (複数可)"
                    selectedSummary={(n) => `${n}日選択中`}
                  />
                </Box>
              )}
            </Stack>
          </Box>

          {/* 実績セクション */}
          <Box data-testid="management-result-actual-section">
            {renderSummaryHeading("実績")}
            <Stack spacing={1.5}>
              {/* 実施済 トグル */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid="management-result-input-completed"
              >
                <CustomCheckbox
                  checked={executed}
                  onChange={() => {
                    setExecuted((p) => !p);
                    markDirty();
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: colors.text.primary,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => {
                    setExecuted((p) => !p);
                    markDirty();
                  }}
                >
                  実施済
                </Typography>
              </Stack>

              {/* 点検タスクで実施済 ON のとき、その月の「実績」がリモート点検かどうかを選択 */}
              {taskType === "点検" && executed && (
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ pt: 0.5 }}
                  data-testid="management-result-actual-inspection-flags"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CustomCheckbox
                      checked={actualIsRemote}
                      onChange={() => {
                        setActualIsRemote((p) => !p);
                        markDirty();
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: colors.text.primary,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => {
                        setActualIsRemote((p) => !p);
                        markDirty();
                      }}
                    >
                      リモート点検
                    </Typography>
                  </Stack>
                </Stack>
              )}

              {/* 実施済 のとき以下を表示 (総合判定結果 → 詳細/簡易セレクト → カレンダー → 実施結果ラベル → 詳細項目) */}
              {executed && (
                <Box data-testid="management-result-execution-records">
                  {/* 総合判定結果 (1行) */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: colors.text.primary,
                        mb: 0.5,
                      }}
                    >
                      総合判定結果
                    </Typography>
                    <ControlledSingleSelect
                      value={totalJudgement}
                      onChange={(v) => {
                        setTotalJudgement(v as TaskResultJudgement | "");
                        markDirty();
                      }}
                      options={JUDGEMENT_OPTIONS}
                      placeholder={
                        aggregatedJudgement
                          ? `自動: ${aggregatedJudgement}`
                          : "判定を選択"
                      }
                    />
                  </Box>
                  {/* 詳細/簡易セレクト (右寄せ) */}
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ mb: 1 }}
                  >
                    <CustomMiddleSelect
                      options={[
                        { value: "detail", label: "詳細" },
                        { value: "simple", label: "簡易" },
                      ]}
                      value={calendarMode}
                      onChange={(v) =>
                        setCalendarMode(v as "simple" | "detail")
                      }
                      size="sm"
                      width="120px"
                      marginBottom={0}
                    />
                  </Stack>
                  {renderCalendar()}
                  {aggregatedJudgement && (
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: colors.text.secondary,
                        mt: 0.5,
                      }}
                    >
                      日次結果からの自動判定: {aggregatedJudgement}（手動指定優先）
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: colors.text.primary,
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    実施結果 ({completedCount}件)
                  </Typography>
                  {calendarMode === "detail" && executionRecords.length > 0 && (
                    <Box>
                      {executionRecords.map((rec, idx) => (
                        <Box key={rec.execution_id} mb={1}>
                          <ExecutionRecordRow
                            index={idx + 1}
                            record={rec}
                            expanded={expandedExecId === rec.execution_id}
                            onToggleExpand={() =>
                              setExpandedExecId((prev) =>
                                prev === rec.execution_id
                                  ? null
                                  : rec.execution_id,
                              )
                            }
                            onUpdate={(patch) =>
                              updateRecord(rec.execution_id, patch)
                            }
                            onRemove={() => removeRecord(rec.execution_id)}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Box>
                <ControlledTextInput
                  label="備考"
                  value={remarks}
                  onChange={(v) => {
                    setRemarks(v);
                    markDirty();
                  }}
                  multiline
                  rows={3}
                  placeholder="備考を入力"
                />
              </Box>
              <Box
                data-testid="management-result-input-other-attachments"
                sx={{
                  "& .MuiTypography-root": { mt: "0 !important" },
                }}
              >
                <ManagedFileUploader
                  label="その他資料"
                  value={otherAttachments}
                  onChange={(next) => {
                    setOtherAttachments(next);
                    markDirty();
                  }}
                />
              </Box>
              <Box sx={{ my: 2 }}>
                <ManagementTaskCustomFieldsAddable
                  section="result"
                  taskType={taskType}
                  values={resultCustomFields}
                  onChange={(next) => {
                    setResultCustomFields(next);
                    markDirty();
                  }}
                  pendingFields={pendingResultCustomFields}
                  onPendingChange={(next) => {
                    setPendingResultCustomFields(next);
                    markDirty();
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
  );

  // ページ表示: /management-task-results/:task_id ルートまたは PreviewDrawer 埋め込み。
  // formBody を CustomDrawer chrome なしで描画し、保存は FixedActionBar。
  // 幅制約: 中身のフォームを 600px に絞り、CustomSectionTitle 等のヘッダは外で full width。
  if (displayMode === "page") {
    return (
      <>
        <Box
          data-entity="management_task_result"
          data-id={`${task_id}:${year}-${String(month).padStart(2, "0")}`}
          // 各セクション (Stack(640) > section Box) 内のフィールド Stack だけを 600px に絞る。
          // セクション見出し (CustomSectionTitle は Box 包み) と外枠は full width のまま
          // → 工事/支出の詳細ページと同じ「見出し100%・入力600px」ルールに合わせる。
          sx={{ "& > * > * > * > .MuiStack-root": { maxWidth: 600 } }}
        >
          {formBody}
        </Box>
        <FixedActionBar
          submitLabel="保存"
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          disabled={!isDirty || isSubmitting}
          embedded={embedded}
        />
        {unsavedConfirmModal}
      </>
    );
  }

  return (
    <>
      <CustomDrawer
        open={open}
        onClose={requestClose}
        anchor="right"
        width={720}
        title={`${taskName} (${year}年${month}月)`}
        showFooter
        footerContent={drawerFooter}
        sx={{
          "& .drawerFooter": {
            padding: "8px 24px",
            borderTop: "none",
          },
        }}
      >
        {formBody}
      </CustomDrawer>
      {unsavedConfirmModal}
    </>
  );
};

const EXEC_ACCORDION_SX = {
  border: `1px solid ${colors.border.light}`,
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { borderRadius: "10px", margin: 0 },
} as const;

const EXEC_ACCORDION_SUMMARY_SX = {
  pl: 1,
  pr: 2,
  py: 0,
  minHeight: 44,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "8px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 44 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "8px 0" },
} as const;

const EXEC_ACCORDION_TRANSITION = { timeout: 180, unmountOnExit: true } as const;

const formatExecSummary = (rec: ExecutionRecord): string => {
  if (!rec.date) return "未入力";
  return `実施日：${formatDateSlash(rec.date)}`;
};

const ExecutionRecordRow: React.FC<{
  index: number;
  record: ExecutionRecord;
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (patch: Partial<ExecutionRecord>) => void;
  onRemove: () => void;
}> = ({ index, record, expanded, onToggleExpand, onUpdate, onRemove }) => {
  const showFindings = !!record.findings && record.findings.trim().length > 0;
  const summary = formatExecSummary(record);

  return (
    <Accordion
      data-testid="management-result-execution-row"
      expanded={expanded}
      onChange={onToggleExpand}
      slotProps={{ transition: EXEC_ACCORDION_TRANSITION }}
      sx={EXEC_ACCORDION_SX}
    >
      <AccordionSummary sx={EXEC_ACCORDION_SUMMARY_SX}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ width: "100%", minWidth: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: expanded ? "#DBF8FF" : "transparent",
                color: expanded ? "#0E9DBC" : colors.text.primary,
                transition: "all 0.2s",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "#DBF8FF",
                  "& svg": { color: "#0E9DBC" },
                },
              }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: "inherit",
                  transition: "transform 180ms",
                  transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: colors.text.primary,
                flexShrink: 0,
              }}
            >
              {index}回目
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: colors.text.primary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {summary}
            </Typography>
          </Stack>
          <Can action="management-task:delete">
            <Box
              component="span"
              role="button"
              aria-label="削除"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                p: 0.5,
                borderRadius: "4px",
                color: colors.text.secondary,
                "&:hover": {
                  color: colors.status.error,
                  backgroundColor: colors.background.hover,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </Box>
          </Can>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <ManagedDateInput
                label="実施日"
                value={record.date}
                onChange={(v) => onUpdate({ date: v })}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ManagedTimeInput
                label="時間"
                value={record.time || ""}
                onChange={(v) => onUpdate({ time: v })}
              />
            </Box>
          </Stack>
          <ControlledTextInput
            label="担当者"
            value={record.checker || ""}
            onChange={(v) => onUpdate({ checker: v })}
            placeholder="担当者名"
          />
          <ControlledSingleSelect
            label="点検結果"
            value={record.result || ""}
            onChange={(v) =>
              onUpdate({ result: v as DailyResult | undefined })
            }
            options={DAILY_RESULT_OPTIONS}
            placeholder="結果を選択"
          />
          <ControlledTextInput
            label="指摘事項"
            value={record.findings || ""}
            onChange={(v) => onUpdate({ findings: v })}
            multiline
            rows={2}
            placeholder="指摘事項があれば記入"
          />
          {showFindings && (
            <Stack
              spacing={1}
              sx={{ pl: 2, borderLeft: `2px solid ${colors.border.light}` }}
              data-testid="management-result-finding-followup"
            >
              <ControlledTextInput
                label="状況"
                value={record.finding_status || ""}
                onChange={(v) => onUpdate({ finding_status: v })}
                placeholder="現在の状況"
              />
              <ControlledTextInput
                label="実施・対応内容"
                value={record.finding_action || ""}
                onChange={(v) => onUpdate({ finding_action: v })}
                multiline
                rows={2}
                placeholder="対応内容"
              />
              <ManagedDateInput
                label="対応日"
                value={record.finding_resolved_date || ""}
                onChange={(v) => onUpdate({ finding_resolved_date: v })}
              />
            </Stack>
          )}
          <ControlledTextInput
            label="備考"
            value={record.note || ""}
            onChange={(v) => onUpdate({ note: v })}
            multiline
            rows={2}
          />
          <Box
            sx={{
              "& .MuiTypography-root": { mt: "0 !important" },
            }}
          >
            <ManagedFileUploader
              label="添付資料"
              value={(record.attachments || []) as unknown[]}
              onChange={(next) => onUpdate({ attachments: next as any })}
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const ManagedDateInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => {
  const { control, setValue, watch } = useForm<{ v: string }>({
    defaultValues: { v: value },
  });
  React.useEffect(() => {
    setValue("v", value);
  }, [value, setValue]);
  React.useEffect(() => {
    const sub = watch((next) => {
      const v = next.v ?? "";
      if (v !== value) onChange(String(v));
    });
    return () => sub.unsubscribe();
  }, [watch, value, onChange]);
  return (
    <CustomCalendarInput
      label={label}
      name="v"
      control={control}
      inputWidth="100%"
      inputWidthSp="100%"
      marginBottom={0}
    />
  );
};

const ManagedTimeInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => {
  return (
    <Box
      sx={{
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          color: colors.text.primary,
          "& svg": {
            width: 24,
            height: 24,
          },
        },
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: colors.text.primary,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
      )}
      <CustomTimeInput value={value} onChange={onChange} />
    </Box>
  );
};

const ManagedFileUploader: React.FC<{
  label: string;
  value: unknown[];
  onChange: (next: unknown[]) => void;
}> = ({ label, value, onChange }) => {
  const { register, setValue, watch } = useForm<{ v: unknown[] }>({
    defaultValues: { v: value },
  });
  React.useEffect(() => {
    setValue("v", value);
  }, [value, setValue]);
  React.useEffect(() => {
    const sub = watch((next) => {
      const v = (next.v as unknown[]) ?? [];
      if (v !== value) onChange(v);
    });
    return () => sub.unsubscribe();
  }, [watch, value, onChange]);
  return (
    <CustomFileUploader
      label={label}
      name="v"
      register={register as any}
      setValue={setValue as any}
      initialImages={[]}
    />
  );
};

const ManagedMultiSelect: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
  options: { value: string; label: string }[];
  label: string;
  placeholder?: string;
  selectedSummary?: (count: number) => string;
}> = ({ value, onChange, options, label, placeholder, selectedSummary }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<{ v: string[] }>({ defaultValues: { v: value } });
  React.useEffect(() => {
    setValue("v", value);
  }, [value, setValue]);
  return (
    <CustomMultiSelect
      label={label}
      name="v"
      control={control}
      errors={errors}
      options={options}
      placeholder={placeholder}
      onChangeValue={(val: any) => {
        const arr: string[] = Array.isArray(val) ? val.map(String) : [];
        onChange(arr);
      }}
      inputWidth="100%"
      inputWidthSp="100%"
      selectedSummary={selectedSummary}
      hasSpacing={false}
    />
  );
};

export default ManagementResultEditDrawer;
